import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { formatExtractedValue, type ImportProvenanceRow } from './importTypes'
import { EmptyState } from '../ui/empty-state'

interface ImportProvenanceFeedProps {
    org: string
    rows: ImportProvenanceRow[]
}

/**
 * The provenance feed (spec §7.5): the latest extracted values with the entity
 * they landed on and a source link opening the document viewer at the page the
 * value came from (`?page=N`, the mode the document detail page already
 * understands — see patterns §17).
 */
export function ImportProvenanceFeed({ org, rows }: ImportProvenanceFeedProps) {
    const { t } = useTranslation('imports')

    return (
        <section data-testid="import-provenance" className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground">{t('provenance.title')}</h2>

            {rows.length === 0 ? (
                <EmptyState title={t('provenance.empty')} />
            ) : (
                <ul className="divide-y divide-border rounded-lg border border-border bg-card">
                    {rows.map((row, index) => (
                        <li
                            key={`${row.entity_uid}-${row.property_code}-${index}`}
                            data-testid="import-provenance-row"
                            className="flex flex-wrap items-baseline justify-between gap-2 p-3 text-sm"
                        >
                            <div className="min-w-0">
                                <Link
                                    to="/$org/assets/$assetUid"
                                    params={{ org, assetUid: row.entity_uid }}
                                    className="font-medium text-foreground hover:underline"
                                    data-testid="import-provenance-entity"
                                >
                                    {row.entity_name || row.entity_uid}
                                </Link>
                                {row.property_code && (
                                    <span className="ml-2 text-muted-foreground">{row.property_code}</span>
                                )}
                            </div>

                            <div className="flex min-w-0 items-baseline gap-3">
                                <span className="truncate font-medium text-foreground">
                                    {formatExtractedValue(row.value)}
                                </span>
                                {row.document_uid && (
                                    <Link
                                        to="/$org/documents/$docUid"
                                        params={{ org, docUid: row.document_uid }}
                                        search={row.page ? { page: row.page } : {}}
                                        className="shrink-0 text-xs text-primary hover:underline"
                                        data-testid="import-provenance-source"
                                    >
                                        {row.document_name || t('provenance.source')}
                                        {row.page ? ` · ${t('provenance.page', { page: row.page })}` : ''}
                                    </Link>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    )
}
